import { get } from 'lodash';
import { addPayeeFormLengths, swiftFormat, invalidFasterPaymentsReferenceCharactersRegex } from '../config';

const {
	name,
	sortCode,
	accountNumber,
	reference,
	displayName,
	internationalPayeeName,
	iban,
	bickOrSwiftMaxLength,
	bankName,
	clearingCode,
	paymentReason,
	chapsName,
	chapsReference,
	otherPaymentReference,
} = addPayeeFormLengths;

export const spacesCheck = (value) => value.trim().replace(/\s\s+/g, ' ');

const numberRegex = (value) => value.replace(/\D+/g, '');
const alphaNumeric = (value) => value.replace(/[^0-9A-Za-z]/g, '');
export const swiftChars = (value) => value.replace(swiftFormat, '');
const addressLine1 = (value) => swiftChars(value).substring(0, addPayeeFormLengths.addressLine1);
const addressLine2 = (value) => swiftChars(value).substring(0, addPayeeFormLengths.addressLine2);
const postalCode = (value) => swiftChars(value).substring(0, addPayeeFormLengths.postalCode);
const city = (value) => swiftChars(value).substring(0, addPayeeFormLengths.city);
const chapsReferenceFormat = (value) => swiftChars(value).substring(0, chapsReference);

export const isInvalidChapsValue = ({ id, value, addPayeeForm, filedFormat }) => {
	const { chapsPaymentsEnable } = addPayeeForm;
	if (chapsPaymentsEnable) {
		const isChapsValidFormat = !value.match(swiftFormat);
		const isChapsStoreChapsValidFormat = !get(addPayeeForm, id, '').match(swiftFormat);
		if (!isChapsValidFormat && !isChapsStoreChapsValidFormat) {
			return value;
		}
	}
	return filedFormat(value);
};

export const addPayeeFormFormatter = {
	name: ({ target: { value } }, addPayeeForm) => {
		const { chapsPaymentsEnable } = addPayeeForm;
		const isChapsValidFormat = !value.match(swiftFormat);
		const isChapsStoreChapsValidFormat = !addPayeeForm.name.match(swiftFormat);
		const isStoreChapsNameValid = addPayeeForm.name.length <= chapsName && isChapsStoreChapsValidFormat;
		const isChapsValid = value.length <= chapsName && isChapsValidFormat;
		const nameRegex = /[^0-9A-Za-z\s&'().,/ -]/g;
		if (chapsPaymentsEnable) {
			if (!isChapsValidFormat && !isChapsStoreChapsValidFormat) {
				return value;
			}
			if (isChapsValid || isStoreChapsNameValid) {
				return value.replace(swiftFormat, '').substring(0, chapsName);
			}
			if (value.length > addPayeeForm.name.length) {
				return value.replace(swiftFormat, '').substring(0, addPayeeForm.name.length);
			}
			return value.replace(swiftFormat, '');
		}
		return value.replace(nameRegex, '').substring(0, name);
	},
	sortCode: ({ target: { value } }) => numberRegex(value).substring(0, sortCode),
	accountNumber: ({ target: { value } }) => numberRegex(value).substring(0, accountNumber),
	reference: ({ target: { value = '' } }) =>
		value.replace(invalidFasterPaymentsReferenceCharactersRegex, '').substring(0, reference),
	displayName: ({ target: { value = '' } }) => value.replace(/[^0-9A-Za-z\s&'().,/ -]/g, '').substring(0, displayName),
	internationalPayeeName: ({ target: { value } }) => swiftChars(value).substring(0, internationalPayeeName),
	payeeAddressLine1: ({ target: { id, value } }, addPayeeForm = {}) =>
		isInvalidChapsValue({ id, value, addPayeeForm, filedFormat: addressLine1 }),
	payeeAddressLine2: ({ target: { id, value } }, addPayeeForm = {}) =>
		isInvalidChapsValue({ id, value, addPayeeForm, filedFormat: addressLine2 }),
	payeePostalCode: ({ target: { id, value } }, addPayeeForm = {}) =>
		isInvalidChapsValue({ id, value, addPayeeForm, filedFormat: postalCode }),
	payeeCity: ({ target: { id, value } }, addPayeeForm = {}) =>
		isInvalidChapsValue({ id, value, addPayeeForm, filedFormat: city }),
	bankAddressLine1: ({ target: { value } }) => addressLine1(value),
	bankAddressLine2: ({ target: { value } }) => addressLine2(value),
	bankPostalCode: ({ target: { value } }) => postalCode(value),
	bankCity: ({ target: { value } }) => city(value),
	iban: ({ target: { value } }) => alphaNumeric(value).substring(0, iban),
	internationalAccountNumber: ({ target: { value } }) => alphaNumeric(value).substring(0, iban),
	bickOrSwift: ({ target: { value } }) => alphaNumeric(value).substring(0, bickOrSwiftMaxLength),
	bankName: ({ target: { value } }) => swiftChars(value).substring(0, bankName),
	clearingCode: ({ target: { value } }) => alphaNumeric(value).substring(0, clearingCode),
	paymentReason: ({ target: { value } }) => swiftChars(value).substring(0, paymentReason),
	chapsReference: ({ target: { id, value } }, addPayeeForm = {}) =>
		isInvalidChapsValue({ id, value, addPayeeForm, filedFormat: chapsReferenceFormat }),
	otherPaymentReference: ({ target: { value = '' } }) =>
		value.replace(/[^0-9A-Za-z&./ ]/g, '').substring(0, otherPaymentReference),
};

export const getConfirmationOfPayeeCopCheckError = () => ({
	cop_reason_code: null,
	reason_code_description: 'CoP Service Unavailable',
	cop_matching_outcome: null,
	matching_outcome: null,
});

export const getConfirmationOfPayeeCopCheckSuccess = (copResult) => ({
	...(get(copResult, 'cop_reason_code') && { cop_reason_code: copResult.cop_reason_code }),
	...(get(copResult, 'reason_code_description') && { reason_code_description: copResult.reason_code_description }),
	cop_matching_outcome: get(copResult, 'cop_matching_outcome') !== undefined ? copResult.cop_matching_outcome : null,
	...(get(copResult, 'matching_outcome') && { matching_outcome: copResult.matching_outcome }),
});

export const getConfirmationOfPayeeNoCopCheck = (payee) => ({
	...(get(payee, 'confirmation_of_payee.matching_outcome') && {
		matching_outcome: payee.confirmation_of_payee.matching_outcome,
	}),
	...(get(payee, 'confirmation_of_payee.cop_reason_code') && {
		cop_reason_code: payee.confirmation_of_payee.cop_reason_code,
	}),
});
